import React, { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';

export type RemoveEnrollmentScope = 'session' | 'term';

export interface RemoveStudentEnrollmentDialogProps {
  isOpen: boolean;
  studentName: string;
  lessonName: string;
  sessionDateLabel: string;
  sessionDateYmd: string;
  bookingId: string;
  /** When false, session removal never offers make-up credit */
  makeupLessonEnabled?: boolean;
  /** Term/full enrollment — show both session and term options */
  isTermEnrollment?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (params: {
    scope: RemoveEnrollmentScope;
    grantMakeupCredit: boolean;
  }) => Promise<void>;
}

export function RemoveStudentEnrollmentDialog({
  isOpen,
  studentName,
  lessonName,
  sessionDateLabel,
  bookingId: _bookingId,
  sessionDateYmd: _sessionDateYmd,
  makeupLessonEnabled = false,
  isTermEnrollment = true,
  isSubmitting = false,
  onClose,
  onConfirm,
}: RemoveStudentEnrollmentDialogProps) {
  const [scope, setScope] = useState<RemoveEnrollmentScope>('session');
  const [grantMakeupCredit, setGrantMakeupCredit] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (isSubmitting) return;
    setScope('session');
    setGrantMakeupCredit(false);
    setError('');
    onClose();
  };

  const handleConfirm = async () => {
    try {
      setError('');
      await onConfirm({
        scope,
        grantMakeupCredit: scope === 'session' && grantMakeupCredit,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to remove student');
    }
  };

  const blockDismiss = (event: { preventDefault(): void }) => {
    if (isSubmitting) event.preventDefault();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className="bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg'
          )}
          onPointerDownOutside={blockDismiss}
          onInteractOutside={blockDismiss}
          onEscapeKeyDown={blockDismiss}
        >
          <DialogHeader>
            <DialogTitle>Remove student</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm text-gray-800 dark:text-gray-200">
            <p>
              Remove <strong>{studentName}</strong> from <strong>{lessonName}</strong> on{' '}
              <strong>{sessionDateLabel}</strong>.
            </p>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                How should this removal work?
              </legend>

              <label className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 cursor-pointer has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  name="remove-scope"
                  className="mt-1"
                  checked={scope === 'session'}
                  onChange={() => setScope('session')}
                  disabled={isSubmitting}
                />
                <span>
                  <span className="font-medium block">Remove from this session only</span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Removes the student from {sessionDateLabel} only. The term booking stays active for
                    other dates in this lesson.
                  </span>
                </span>
              </label>

              {isTermEnrollment && (
                <label className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 cursor-pointer has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="remove-scope"
                    className="mt-1"
                    checked={scope === 'term'}
                    onChange={() => setScope('term')}
                    disabled={isSubmitting}
                  />
                  <span>
                    <span className="font-medium block">Cancel term enrollment</span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      Marks the booking as cancelled and removes the student from all remaining
                      sessions in this lesson. The record stays in History and can be restored later.
                    </span>
                  </span>
                </label>
              )}
            </fieldset>

            {scope === 'session' && makeupLessonEnabled && (
              <div className="flex items-start gap-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                <Checkbox
                  id="grant-makeup-credit"
                  checked={grantMakeupCredit}
                  onCheckedChange={(checked) => setGrantMakeupCredit(checked === true)}
                  disabled={isSubmitting}
                />
                <Label htmlFor="grant-makeup-credit" className="text-sm font-normal leading-snug cursor-pointer">
                  Grant a make-up session credit
                </Label>
              </div>
            )}

            {scope === 'term' && (
              <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/90 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-950 dark:text-amber-100">
                This cancels the booking (not just one session). The student will be notified by email.
                To issue a refund for a paid enrollment, process it from <strong>Payments</strong>{' '}
                after confirming.
              </div>
            )}

            {scope === 'session' && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The student or guardian will receive an email about this change.
                {grantMakeupCredit && makeupLessonEnabled
                  ? ' They will also receive a make-up credit notification if a credit is granted.'
                  : ''}
              </p>
            )}

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isSubmitting}
                onClick={() => void handleConfirm()}
                className="gap-1.5"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Working…
                  </>
                ) : scope === 'term' ? (
                  'Cancel term enrollment'
                ) : (
                  'Remove from session'
                )}
              </Button>
            </div>
          </div>

          <DialogPrimitive.Close
            type="button"
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            disabled={isSubmitting}
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
